// =====================================================================
// Faz L (2026-05-06) — Sales Funnel
// =====================================================================
// 6-aşamalı satış hunisi: site nerede kaybediyor sorusunu cevaplar.
// Her aşamada %top + %prev conversion oranı + en kritik drop-off uyarısı.
//
// Endpoint: GET /api/admin/insights/funnel?days=30
//
// Yorumlama rehberi (B2B plastik ambalaj sektör tipik):
//   Visit → Product:    %50-70 normal (yüksek = anlamlı landing)
//   Product → Cart:     %5-15 normal (B2B'de quote süreci uzun)
//   Cart → Checkout:    %40-60 normal (form karmaşıksa düşer)
//   Checkout → Order:   %60-80 normal (ödeme/kargo seçimi friction)
//   Order → Delivered:  %85+   olmalı (düşükse iptal/iade sorunu)
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, CircularProgress, Alert,
  IconButton, Tooltip, ToggleButton, ToggleButtonGroup, LinearProgress
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import API from "../services/api";

const fmtMoney = (v) => v == null ? "—" :
  Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";
const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");

// Aşama-spesifik renk paleti (huninin daralmasını görsel desteklesin)
// Yeşil → mavi → turuncu → kırmızı (daha derine inildikçe daha kritik)
const STAGE_COLORS = ["#10b981", "#22c55e", "#3b82f6", "#8b5cf6", "#f59e0b", "#ef4444"];

// Conversion rate (önceki aşamaya göre %) → renk
const convColor = (pct, stageIdx) => {
  if (stageIdx === 0) return "#64748b";       // İlk aşamada conversion yok
  if (pct >= 60) return "#10b981";
  if (pct >= 30) return "#3b82f6";
  if (pct >= 10) return "#f59e0b";
  return "#ef4444";
};

export default function SalesFunnel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(30);

  const load = (d = days) => {
    setLoading(true);
    API.get(`/api/admin/insights/funnel?days=${d}`)
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(days), [days]);

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const stages = data.stages || [];
  const wd = data.worst_drop;
  const sources = data.sources || {};
  const totalSourceViews = (sources.web || 0) + (sources.mobile || 0);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Hero */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Sales Funnel</Typography>
          <Typography variant="body2" color="text.secondary">
            Ziyaret → Sipariş yolculuğu — nerede kayıp oluyor?
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FilterAltIcon sx={{ color: "#64748b", fontSize: 18 }} />
          <ToggleButtonGroup
            size="small" value={days} exclusive
            onChange={(_, v) => v && setDays(v)}
          >
            <ToggleButton value={7}>7g</ToggleButton>
            <ToggleButton value={30}>30g</ToggleButton>
            <ToggleButton value={90}>90g</ToggleButton>
            <ToggleButton value={365}>1y</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Yenile">
            <IconButton onClick={() => load(days)}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Worst drop-off uyarısı */}
      {wd && wd.from && wd.lost > 0 && (
        <Alert
          severity="warning"
          icon={<TrendingDownIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <strong>En büyük kayıp:</strong> {wd.from} → {wd.to} adımında <strong>%{wd.pct}</strong>
          ({fmtNum(wd.lost)} kişi) düşüş var. Bu adıma odaklanmak en hızlı kazancı verir.
        </Alert>
      )}

      {/* Funnel görsel — 6 yatay bar, sıra ile daralan */}
      <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Huni Görünümü</Typography>
        {stages.map((s, i) => {
          const widthPct = Math.max(s.pct_of_top, 5);  // min %5 görünür kalsın
          return (
            <Box key={s.code} sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Box>
                  <Typography fontWeight={700} fontSize={14}>
                    {i + 1}. {s.label}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">{s.desc}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" fontWeight={800} sx={{ color: STAGE_COLORS[i] }}>
                    {fmtNum(s.count)}
                  </Typography>
                  {i > 0 && (
                    <Chip
                      label={`%${s.pct_of_prev}`}
                      size="small"
                      sx={{
                        bgcolor: convColor(s.pct_of_prev, i),
                        color: "white", fontWeight: 700, fontSize: 11,
                        minWidth: 50
                      }}
                      title={`Bir önceki aşamaya göre conversion: ${s.pct_of_prev}%`}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{
                position: "relative", height: 32, borderRadius: 1,
                bgcolor: "#f1f5f9", overflow: "hidden"
              }}>
                <Box sx={{
                  width: `${widthPct}%`, height: "100%",
                  background: `linear-gradient(90deg, ${STAGE_COLORS[i]} 0%, ${STAGE_COLORS[i]}cc 100%)`,
                  transition: "width 0.4s ease",
                  display: "flex", alignItems: "center", justifyContent: "flex-end",
                  pr: 1.5, color: "white", fontWeight: 700, fontSize: 12
                }}>
                  {s.pct_of_top}% top
                </Box>
              </Box>
              {i < stages.length - 1 && s.drop_count > 0 && (
                <Typography variant="caption" sx={{ color: "#ef4444", display: "block", mt: 0.5, fontSize: 11 }}>
                  ↓ {fmtNum(s.drop_count)} kayıp ({stages[i].count > 0 ? Math.round((s.drop_count / stages[i].count) * 100) : 0}% drop)
                </Typography>
              )}
            </Box>
          );
        })}
      </Card>

      {/* Yan KPI'lar */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              ÖDENEN CİRO ({days} gün)
            </Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0" mt={0.5}>
              {fmtMoney(data.revenue.placed)}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Teslim edilen: <strong>{fmtMoney(data.revenue.delivered)}</strong>
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              GENEL CONVERSION
            </Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981" mt={0.5}>
              %{stages.length > 0 ? stages[stages.length - 2].pct_of_top : 0}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Ziyaretten siparişe (cancelled hariç)
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              TRAFİK KAYNAĞI
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <LanguageIcon sx={{ color: "#3b82f6", fontSize: 18 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>Web</Typography>
                <Typography fontWeight={700}>
                  {fmtNum(sources.web)} ({totalSourceViews > 0 ? Math.round((sources.web / totalSourceViews) * 100) : 0}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={totalSourceViews > 0 ? (sources.web / totalSourceViews) * 100 : 0}
                sx={{ height: 6, borderRadius: 3, mb: 1.5,
                  "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" }
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <PhoneIphoneIcon sx={{ color: "#10b981", fontSize: 18 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>Mobil App</Typography>
                <Typography fontWeight={700}>
                  {fmtNum(sources.mobile)} ({totalSourceViews > 0 ? Math.round((sources.mobile / totalSourceViews) * 100) : 0}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={totalSourceViews > 0 ? (sources.mobile / totalSourceViews) * 100 : 0}
                sx={{ height: 6, borderRadius: 3,
                  "& .MuiLinearProgress-bar": { bgcolor: "#10b981" }
                }}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Yorumlama rehberi */}
      <Card sx={{ p: 2.5, borderRadius: 3, mt: 3, bgcolor: "#f8fafc" }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>📚 Sektör Tipik Conversion (B2B Plastik Ambalaj)</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={4}>
            <Typography variant="caption" color="text.secondary">Visit → Product</Typography>
            <Typography fontWeight={600} fontSize={13}>%50-70 normal</Typography>
          </Grid>
          <Grid item xs={6} md={4}>
            <Typography variant="caption" color="text.secondary">Product → Cart</Typography>
            <Typography fontWeight={600} fontSize={13}>%5-15 normal (B2B yavaş)</Typography>
          </Grid>
          <Grid item xs={6} md={4}>
            <Typography variant="caption" color="text.secondary">Cart → Checkout</Typography>
            <Typography fontWeight={600} fontSize={13}>%40-60 normal</Typography>
          </Grid>
          <Grid item xs={6} md={4}>
            <Typography variant="caption" color="text.secondary">Checkout → Order</Typography>
            <Typography fontWeight={600} fontSize={13}>%60-80 normal</Typography>
          </Grid>
          <Grid item xs={6} md={4}>
            <Typography variant="caption" color="text.secondary">Order → Delivered</Typography>
            <Typography fontWeight={600} fontSize={13}>%85+ olmalı</Typography>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
}
